<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import logoDark from "@/assets/cover-dark.png";
import logoLight from "@/assets/cover-light.png";

// Theme-aware logo
const currentTheme = ref('dark');

const logo = computed(() => {
  return currentTheme.value === 'light' ? logoLight : logoDark;
});

// Detect theme changes
const updateTheme = () => {
  const html = document.documentElement;
  currentTheme.value = html.getAttribute('data-theme') || 'dark';
};

onMounted(() => {
  updateTheme();

  // Watch for theme changes
  const observer = new MutationObserver(updateTheme);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme', 'class']
  });

  // Store observer to disconnect later
  window._themeObserver = observer;
});

onUnmounted(() => {
  if (window._themeObserver) {
    window._themeObserver.disconnect();
  }
});
</script>

<template>
  <footer class="brutalist-footer">
    <div class="footer-content">
              <!-- Sección de Columnas (Ahora arriba) -->
              <div class="footer-columns">
                <!-- Columna Legal -->
                <div class="footer-column">
                  <h3 class="footer-column-title">Legal</h3>
                  <ul class="footer-links">
                    <li>
                      <router-link to="/legal/terms"
                        >Términos y Condiciones</router-link
                      >
                    </li>
                    <li>
                      <router-link to="/legal/privacy"
                        >Política de Privacidad</router-link
                      >
                    </li>
                    <li>
                      <router-link to="/legal/cookies"
                        >Política de Cookies</router-link
                      >
                    </li>
                    <li>
                      <router-link to="/legal/contact">Contacto</router-link>
                    </li>
                  </ul>
                </div>

                <!-- Columna Escuchar (oculta en móvil) -->
                <div class="footer-column footer-column--hide-mobile">
                  <h3 class="footer-column-title">Escuchar</h3>
                  <ul class="footer-links">
                    <li>
                      <a
                        href="https://tunein.com/radio/Astra-Riot-s188076/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i class="fa-solid fa-radio"></i> TuneIn
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://open.spotify.com/user/ultravoz?si=203d293e2b954039"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i class="fa-brands fa-spotify"></i> Spotify
                      </a>
                    </li>
                  </ul>
                </div>

                <!-- Columna Social (oculta en móvil) -->
                <div class="footer-column footer-column--hide-mobile">
                  <h3 class="footer-column-title">Social</h3>
                  <ul class="footer-links">
                    <li>
                      <a
                        href="https://bsky.app/profile/astra-radio.bsky.social"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i class="fa-brands fa-bluesky"></i> Bluesky
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.facebook.com/AstraRadioLab"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i class="fa-brands fa-facebook"></i> Facebook
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.instagram.com/astralab.fm/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i class="fa-brands fa-instagram"></i> Instagram
                      </a>
                    </li>
                  </ul>
                </div>

                <!-- Columna Contacto -->
                <div class="footer-column">
                  <h3 class="footer-column-title">Contacto</h3>
                  <div class="footer-contact">
                    <div class="footer-contact-item">
                      <a href="mailto:soporte@astra.fm" class="footer-contact-email">
                        soporte@astra.fm
                      </a>
                      <p class="footer-contact-desc">
                        Soporte técnico, problemas con la app y acceso beta
                      </p>
                    </div>
                    <div class="footer-contact-item">
                      <a href="mailto:info@astra.fm" class="footer-contact-email">
                        info@astra.fm
                      </a>
                      <p class="footer-contact-desc">
                        Colaboraciones, prensa e información general
                      </p>
                    </div>
                  </div>
                </div>
              </div>

      <!-- Sección Inferior: Logo y Copyright (Ahora abajo, en línea) -->
      <div class="footer-header">
        <div class="footer-logo">
          <img :src="logo" alt="Astra logo" class="footer-logo-img" />
        </div>
        <p class="footer-copyright">
          <i class="fa-regular fa-copyright"></i> Copyright Astra 2025 ·
          Es una idea y creación de
          <a
            href="https://pablogoris.com"
            target="_blank"
            rel="noopener noreferrer"
            >Pablo Goris</a
          >
          <span class="footer-license-inline">· Licencia SGAERRDD/4/1419/1220</span>
        </p>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/brutalist-footer.scss";
</style>
